import {
  AsyncPipe, NgClass, NgFor, NgIf, NgStyle,
} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Renderer2,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { CalendarModule } from 'primeng/calendar';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { map } from 'rxjs';
import { FormDataService } from '@shared/core/services/form-data.service';
import { BoldFontWeightDirective } from '@shared/core/directives/bold-font-weight.directive';

@Component({
  selector: 'app-datepicker',
  standalone: true,
  imports: [
    FormsModule,
    CalendarModule,
    NgClass,
    NgIf,
    NgFor,
    NgStyle,
    AsyncPipe,
  ],
  templateUrl: './datepicker.component.html',
  styleUrl: './datepicker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DatepickerComponent,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: DatepickerComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatepickerComponent implements ControlValueAccessor, Validator {
  minDateInput = input<Date>();

  maxDateInput = input<Date>();

  isRange = input<boolean>(false);

  disabled = input<boolean>(false);

  renderer2 = inject(Renderer2);

  formService = inject(FormDataService);

  date!: Date[] | Date | null;

  bpObserver = inject(BreakpointObserver);

  breakpoints = Breakpoints;

  numberOfMonths$ = this.bpObserver.observe('(max-width:991px)').pipe(
    map((val) => {
      if (!val.matches) {
        return this.isRange() ? 2 : 1;
      }
      return 12;
    }),
  );

  isDesk$ = this.bpObserver.observe('(max-width:991px)').pipe(
    map((val) => !val.matches),
  );

  private elemRef = inject(ElementRef);

  private cdRef = inject(ChangeDetectorRef);

  onChange = (date: Date[] | Date | null) => {};

  onTouched = () => {};

  writeValue(value: Date | Date[]): void {
    this.date = value;
    this.cdRef.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return control.value && this.isValidDate ? null : { date: true };
  }

  get isValidDate() {
    if (this.isRange()) {
      const dateArray = this.date as Date[];
      return dateArray.every((date) => !!date);
    }
    return !!this.date;
  }

  onClickCalendar() {
    const selectedRange = this.elemRef.nativeElement.querySelectorAll('span.p-highlight');

    if (selectedRange.length) {
      selectedRange.forEach((d: Element) => d.classList.add('highlight'));
    }

    const dateFrom = selectedRange?.[0];

    if (this.date && this.isRange() && selectedRange.length) {
      if (selectedRange.length === 1) {
        this.removeCustomClasses();
      }
      if (selectedRange.length > 1) {
        const dateTo = selectedRange?.[selectedRange.length - 1];
        this.renderer2.addClass(dateTo, 'active');
        this.renderer2.addClass(dateTo, 'last');
      }

      this.renderer2.addClass(dateFrom, 'first');
      this.renderer2.addClass(dateFrom, 'active');
    }
    if (!this.isRange() && dateFrom) {
      this.removeCustomClasses();
      this.renderer2.addClass(dateFrom, 'active');
      this.renderer2.addClass(dateFrom, 'first');
      this.renderer2.addClass(dateFrom, 'last');
    }
  }

  onDateSelect() {
    this.onChange(this.date);
    if (
      this.date
      && Array.isArray(this.date)
      && this.date.length === 2
      && this.date.every((d) => d)
    ) {
      this.formService.updateFormData({ dateRange: this.date });
      this.formService.updateSummaryData({ dateRange: this.date });
    }
  }

  removeCustomClasses() {
    const activeDates = this.elemRef.nativeElement.querySelectorAll('.active');
    if (activeDates.length) {
      activeDates.forEach((date: Element) => {
        date.classList.remove('active');
        date.classList.remove('first');
        date.classList.remove('last');
      });
    }
    const highlights = this.elemRef.nativeElement.querySelectorAll('.highlight');
    if (highlights.length) {
      highlights.forEach((d: Element) => d.classList.remove('highlight'));
    }
  }
}
